
import apiClient from "@/shared/services/api/apiClient";

export interface MasterDatabaseRecord {
  id: string;

  // Internal link to the original portal application.
  // Null for CSV/Excel imported and manually created records.
  applicationId: string | null;

  candidateId: string | null;
  ageing: string | null;
  sharedCandidate: string | null;
  candidateBlacklisted: string | null;
  serialNo: string | null;
  timestamp: string | null;
  hrName: string | null;
  candidateName: string | null;
  contactNo: string | null;
  location: string | null;
  entity: string | null;
  position: string | null;
  hodName: string | null;
  reportingManager: string | null;
  source: string | null;
  subSource: string | null;
  screeningDate: string | null;
  callStatus: string | null;
  currentCompany: string | null;
  totalExperience: string | null;
  realStateExperience: string | null;
  currentInhand: string | null;
  expectedInhand: string | null;
  pfNoPf: string | null;
  noticePeriod: string | null;
  interviewLineup: string | null;
  interviewLinedupDate: string | null;
  hrInterviewDate: string | null;
  hrInterviewName: string | null;
  hrInterviewStatus: string | null;
  business1Date: string | null;
  business1InterviewerName: string | null;
  logs: string | null;
  createdAt?: string;
  updatedAt?: string;
}

// applicationId is generated/managed by the backend, not by manual entry.
export type MasterDatabaseRecordPayload = Partial<
  Omit<
    MasterDatabaseRecord,
    "id" | "applicationId" | "logs" | "createdAt" | "updatedAt"
  >
>;

export interface MasterDatabaseStats {
  total: number;
  new: number;
  inProgress: number;
  offered: number;
  hired: number;
  rejected: number;
  voiceCallCompleted: number;
}

export interface MasterDatabasePagination {
  currentPage: number;
  perPage: number;
  totalRecords: number;
  totalPages: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
}

export interface MasterDatabaseListResponse {
  success: boolean;
  message: string;
  data: MasterDatabaseRecord[];
  pagination: MasterDatabasePagination;
  stats: MasterDatabaseStats;
}

export interface MasterDatabaseRecordResponse {
  success: boolean;
  message: string;
  data: MasterDatabaseRecord;
}

export interface MasterDatabaseImportResponse {
  success: boolean;
  message: string;
  data: {
    importedCount: number;
  };
}

export interface GetMasterDatabaseParams {
  page?: number;
  limit?: number;
  search?: string;
}

/**
 * Fetch Master DB records with pagination, search and backend statistics.
 */
export const getMasterDatabaseRecords = async (
  params: GetMasterDatabaseParams = {},
): Promise<MasterDatabaseListResponse> => {
  const response = await apiClient.get("/master-db", {
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 25,
      search: params.search ?? "",
    },
  });

  return response.data;
};

/**
 * Create a manually entered Master DB record.
 * The backend generates the ID and audit logs.
 */
export const createMasterDatabaseRecord = async (
  payload: MasterDatabaseRecordPayload,
): Promise<MasterDatabaseRecordResponse> => {
  const response = await apiClient.post("/master-db", payload);

  return response.data;
};

/**
 * Fetch one Master DB record by its ID.
 */
export const getMasterDatabaseRecordById = async (
  id: string,
): Promise<MasterDatabaseRecordResponse> => {
  const response = await apiClient.get(
    `/master-db/${encodeURIComponent(id)}`,
  );

  return response.data;
};

/**
 * Import an Excel or CSV file into Master DB.
 * The backend expects the multipart field name to be "file".
 */
export const importMasterDatabaseRecords = async (
  file: File,
): Promise<MasterDatabaseImportResponse> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await apiClient.post(
    "/master-db/import",
    formData,
  );

  return response.data;
};

/**
 * Update an existing Master DB record.
 */
export const updateMasterDatabaseRecord = async (
  id: string,
  payload: MasterDatabaseRecordPayload,
): Promise<MasterDatabaseRecordResponse> => {
  const response = await apiClient.patch(
    `/master-db/${encodeURIComponent(id)}`,
    payload,
  );

  return response.data;
};
